// =============================================================================
// app/src/lib/fiado.ts — o fiado POR PESSOA e os números da cobrança.
//
// Pedido do dono (26/09/2026): "uma tela separada em fiado onde eu consigo
// visualizar os fiados somente por pessoa e, quando clico, ele abre com as
// dívidas" — mais o prazo médio da dívida e as outras estatísticas que
// importam para cobrar.
//
// Tudo aqui é conta sobre o que o banco JÁ entregou: listar_fiados (quem deve,
// quanto, há quantos dias) e os acertos com o dia em que o cliente pagou
// (contas_receber_recebimentos, 042/059). Nada é gravado — número derivado não
// se armazena (regra 4) — e nada aqui decide quem vê: isso é a RLS, que só
// abre essas tabelas para gerente e dono.
//
// Funções puras e testadas linha a linha (fiado.test.ts), pela mesma régua de
// listas.ts e contasPagar.ts.
// =============================================================================
import type { ContaReceberRecebimento, FiadoRow } from '@core/types';
import { hojeISO } from './formato';
import { normalizar, porNumero, porTexto } from './listas';

/** Depois de quantos dias uma dívida SEM data combinada vira atraso. */
export const DIAS_PARA_ATRASO = 30;

/**
 * A dívida está atrasada?
 *
 * Duas réguas, nesta ordem, porque nem toda venda fiada combina data: se há
 * `vencimento`, é ELE que manda — foi o que se combinou com o cliente, e 40
 * dias com prazo de 60 não é atraso nenhum. Sem data combinada, vale o costume
 * da casa: 30 dias.
 *
 * UMA função para todos os usos (a tarja do cartão, o filtro "só as
 * atrasadas", o total atrasado da pessoa) de propósito: com duas réguas, o
 * filtro mostraria linha sem tarja e a tela se contradiria na cara de quem
 * cobra.
 */
export function estaAtrasada(f: Pick<FiadoRow, 'vencimento' | 'dias_em_aberto'>, hoje: string): boolean {
  return f.vencimento ? f.vencimento < hoje : f.dias_em_aberto > DIAS_PARA_ATRASO;
}

/** A conta ainda cobra: nem quitada, nem dada como perdida. */
export function estaAberta(f: Pick<FiadoRow, 'perdido_em' | 'saldo'>): boolean {
  return !f.perdido_em && Number(f.saldo) > 0;
}

/** O que a estatística precisa de cada acerto: de qual conta, quanto e quando. */
export const COLUNAS_DO_ACERTO = [
  'conta_id', 'valor', 'recebido_em',
] as const satisfies ReadonlyArray<keyof ContaReceberRecebimento>;

export type AcertoDoFiado = Pick<ContaReceberRecebimento, (typeof COLUNAS_DO_ACERTO)[number]>;

// --- Dias -------------------------------------------------------------------

const DIA_MS = 86_400_000;

/** Meia-noite UTC de uma data pura ('2026-09-12'). Por Date.UTC, e nunca
 *  `new Date(texto)`: a conta de dias não pode depender do fuso do aparelho. */
function meiaNoiteUTC(dia: string): number {
  return Date.UTC(Number(dia.slice(0, 4)), Number(dia.slice(5, 7)) - 1, Number(dia.slice(8, 10)));
}

/** Dias corridos de uma data pura a outra ('2026-09-01' → '2026-09-11' = 10). */
export function diasEntre(de: string, ate: string): number {
  return Math.round((meiaNoiteUTC(ate) - meiaNoiteUTC(de)) / DIA_MS);
}

/** A data pura `n` dias depois (ou antes, com n negativo). */
export function somarDias(dia: string, n: number): string {
  return new Date(meiaNoiteUTC(dia) + n * DIA_MS).toISOString().slice(0, 10);
}

/** O DIA da venda, no fuso da loja. `vendida_em` é timestamptz: fatiar o texto
 *  pegaria o dia em UTC, e a venda das 22h cairia no dia seguinte. */
function diaDaVenda(vendidaEm: string): string {
  return hojeISO(new Date(vendidaEm));
}

// --- De quem é cada conta ------------------------------------------------------

/** O nome como chave: sem acento, sem caixa, espaço simples. */
export function chaveDoNome(nome: string): string {
  return normalizar(nome).replace(/\s+/g, ' ');
}

/**
 * De quem é cada conta (id da conta → chave da pessoa). O CADASTRO manda
 * quando existe; sem cadastro, o nome.
 *
 * O caso que obrigou a segunda regra, medido em produção em 26/09/2026: 7 dos
 * 40 nomes do fiado aparecem COM cadastro numa venda e SEM cadastro noutra — o
 * balcão digitou o nome em vez de escolher o cliente. Agrupar só pelo cadastro
 * partiria essas pessoas em duas linhas, e cada metade pareceria dever menos
 * do que deve.
 *
 * Por isso a conta sem cadastro vai para o cadastro de MESMO nome (sem acento,
 * caixa ou espaço sobrando). Nome que casa com DOIS cadastros — homônimos —
 * não é adivinhado: fica numa linha própria, pelo nome. Juntar dívida de duas
 * pessoas é pior do que mostrar uma pessoa em duas linhas.
 */
export function chavesDasPessoas(
  contas: ReadonlyArray<Pick<FiadoRow, 'id' | 'cliente' | 'customer_id'>>,
): Map<string, string> {
  const cadastrosDoNome = new Map<string, Set<string>>();
  for (const c of contas) {
    if (!c.customer_id) continue;
    const nome = chaveDoNome(c.cliente);
    const ids = cadastrosDoNome.get(nome) ?? new Set<string>();
    ids.add(c.customer_id);
    cadastrosDoNome.set(nome, ids);
  }

  const chaves = new Map<string, string>();
  for (const c of contas) {
    if (c.customer_id) {
      chaves.set(c.id, `cadastro:${c.customer_id}`);
      continue;
    }
    const nome = chaveDoNome(c.cliente);
    const ids = cadastrosDoNome.get(nome);
    const unico = ids && ids.size === 1 ? [...ids][0] : undefined;
    chaves.set(c.id, unico ? `cadastro:${unico}` : `nome:${nome}`);
  }
  return chaves;
}

// --- A pessoa -----------------------------------------------------------------

export interface PessoaDoFiado {
  /** 'cadastro:<id>' ou 'nome:<nome sem acento>' — a mesma entre recargas,
   *  e é por ela que a linha continua aberta depois de um acerto. */
  chave: string;
  nome: string;
  /** Todas as contas dela, da venda mais antiga para a mais nova. */
  contas: FiadoRow[];
  /** Só as que ainda cobram (nem quitadas, nem dadas como perdidas). */
  abertas: FiadoRow[];
  /** O que ela deve hoje. */
  saldo: number;
  atrasadas: number;
  saldoAtrasado: number;
  /** Dias da dívida aberta mais velha; null quando não deve nada. */
  diasMaisAntiga: number | null;
  /** Idade média do que ela deve, pesada pelo saldo; null quando não deve. */
  idadeMedia: number | null;
  /** Tudo o que ela já levou fiado (quitado, aberto e perdido). */
  comprado: number;
  pago: number;
  /** Quantos acertos ela fez — um acerto pode cobrir parte de uma venda. */
  pagamentos: number;
  desconto: number;
  devolvido: number;
  /** O que ficou devendo nas contas dadas como perdidas. */
  perdido: number;
  /** Em quantos dias ela paga, pesado pelo valor; null sem nenhum acerto. */
  prazoMedio: number | null;
  /** O dia do último acerto e quanto entrou NAQUELE dia (somando as vendas). */
  ultimoPagamento: { dia: string; valor: number } | null;
}

function somar<T>(lista: readonly T[], valor: (item: T) => number): number {
  return lista.reduce((s, item) => s + valor(item), 0);
}

function acertosPorConta(acertos: readonly AcertoDoFiado[]): Map<string, AcertoDoFiado[]> {
  const mapa = new Map<string, AcertoDoFiado[]>();
  for (const a of acertos) {
    const lista = mapa.get(a.conta_id) ?? [];
    lista.push(a);
    mapa.set(a.conta_id, lista);
  }
  return mapa;
}

/**
 * Em quantos dias o dinheiro do fiado volta: da compra ao pagamento, PESADO
 * PELO VALOR — um acerto de R$ 1.000 conta cem vezes mais que um de R$ 10.
 *
 * Por que pesado: a pergunta de quem cobra é "quanto tempo o MEU DINHEIRO fica
 * na rua", e a média simples por acerto responde outra coisa. Medido em
 * 26/09/2026: 9,5 dias pela média simples contra 15,9 pesados — as dívidas
 * grandes demoram mais, e é nelas que está o dinheiro.
 *
 * Acerto com data ANTERIOR à venda (digitado errado — o banco só recusa data
 * futura) conta como zero dias, e não como prazo negativo que puxaria a média
 * para baixo.
 */
function prazoPesado(contas: readonly FiadoRow[], porConta: Map<string, AcertoDoFiado[]>): number | null {
  let valor = 0;
  let valorVezesDias = 0;
  for (const c of contas) {
    const dia = diaDaVenda(c.vendida_em);
    for (const a of porConta.get(c.id) ?? []) {
      const v = Number(a.valor);
      valor += v;
      valorVezesDias += v * Math.max(0, diasEntre(dia, a.recebido_em));
    }
  }
  return valor > 0 ? valorVezesDias / valor : null;
}

/** Idade média do que está em aberto, pesada pelo saldo. */
function idadePesada(abertas: readonly FiadoRow[]): number | null {
  const saldo = somar(abertas, (f) => Number(f.saldo));
  return saldo > 0 ? somar(abertas, (f) => Number(f.saldo) * f.dias_em_aberto) / saldo : null;
}

function porVenda(a: FiadoRow, b: FiadoRow): number {
  return Date.parse(a.vendida_em) - Date.parse(b.vendida_em);
}

function montarPessoa(
  chave: string, lista: readonly FiadoRow[], porConta: Map<string, AcertoDoFiado[]>, hoje: string,
): PessoaDoFiado {
  const contas = [...lista].sort(porVenda);
  const abertas = contas.filter(estaAberta);
  const atrasadas = abertas.filter((f) => estaAtrasada(f, hoje));

  // O NOME da linha: o da venda mais recente COM cadastro — o cadastro é o
  // nome escolhido; o digitado no balcão às vezes vem em minúscula ou pela
  // metade. Sem nenhum cadastro, o da venda mais recente.
  const comCadastro = contas.filter((f) => f.customer_id);
  const nome = (comCadastro[comCadastro.length - 1] ?? contas[contas.length - 1])?.cliente ?? '';

  const acertos = contas.flatMap((f) => porConta.get(f.id) ?? []);
  let ultimoPagamento: PessoaDoFiado['ultimoPagamento'] = null;
  for (const a of acertos) {
    if (!ultimoPagamento || a.recebido_em > ultimoPagamento.dia) {
      ultimoPagamento = { dia: a.recebido_em, valor: Number(a.valor) };
    } else if (a.recebido_em === ultimoPagamento.dia) {
      ultimoPagamento.valor += Number(a.valor);
    }
  }

  return {
    chave,
    nome,
    contas,
    abertas,
    saldo: somar(abertas, (f) => Number(f.saldo)),
    atrasadas: atrasadas.length,
    saldoAtrasado: somar(atrasadas, (f) => Number(f.saldo)),
    diasMaisAntiga: abertas.length > 0 ? Math.max(...abertas.map((f) => f.dias_em_aberto)) : null,
    idadeMedia: idadePesada(abertas),
    comprado: somar(contas, (f) => Number(f.valor)),
    pago: somar(contas, (f) => Number(f.recebido)),
    // A contagem do SERVIDOR (listar_fiados), e não a dos acertos que vieram
    // à parte: se aquela consulta falhar, a tela não pode dizer "já pagou
    // R$ 500 em 0 pagamentos".
    pagamentos: somar(contas, (f) => f.recebimentos),
    desconto: somar(contas, (f) => Number(f.desconto)),
    devolvido: somar(contas, (f) => Number(f.devolvido)),
    perdido: somar(contas.filter((f) => f.perdido_em), (f) => Math.max(0, Number(f.saldo))),
    prazoMedio: prazoPesado(contas, porConta),
    ultimoPagamento,
  };
}

/** O fiado agrupado por pessoa — todas as pessoas que aparecem nas contas,
 *  inclusive quem já quitou tudo (a tela decide se mostra). */
export function pessoasDoFiado(
  contas: readonly FiadoRow[], acertos: readonly AcertoDoFiado[], hoje: string,
): PessoaDoFiado[] {
  const chaves = chavesDasPessoas(contas);
  const grupos = new Map<string, FiadoRow[]>();
  for (const f of contas) {
    const chave = chaves.get(f.id) ?? `nome:${chaveDoNome(f.cliente)}`;
    const lista = grupos.get(chave) ?? [];
    lista.push(f);
    grupos.set(chave, lista);
  }
  const porConta = acertosPorConta(acertos);
  return [...grupos].map(([chave, lista]) => montarPessoa(chave, lista, porConta, hoje));
}

// --- O fiado da loja inteira ----------------------------------------------------

/** As faixas de idade da dívida em aberto. O corte dos 30 dias é o mesmo do
 *  atraso (DIAS_PARA_ATRASO): as duas de cima são o que já passou do costume. */
export const FAIXAS_DE_IDADE = [
  { rotulo: 'Até 15 dias', ate: 15 },
  { rotulo: '16 a 30 dias', ate: DIAS_PARA_ATRASO },
  { rotulo: '31 a 60 dias', ate: 60 },
  { rotulo: 'Mais de 60 dias', ate: Number.POSITIVE_INFINITY },
] as const;

export interface FaixaDeIdade {
  rotulo: string;
  valor: number;
  contas: number;
  /** Passou dos 30 dias — a faixa já é atraso para quem não combinou data. */
  passouDoCostume: boolean;
}

/** Os maiores devedores entram juntos num número só: "os 3 maiores devem X%". */
export const QUANTOS_MAIORES = 3;

/** A janela do "últimos 30 dias", hoje incluído. */
export const DIAS_DA_JANELA = 30;

export interface ResumoDoFiado {
  totalAberto: number;
  contasAbertas: number;
  pessoasDevendo: number;
  atrasado: number;
  contasAtrasadas: number;
  pessoasAtrasadas: number;
  /** Da compra ao pagamento, pesado pelo valor; null sem nenhum acerto. */
  prazoMedio: number | null;
  /** Idade do que está na rua hoje, pesada pelo saldo; null sem dívida. */
  idadeMedia: number | null;
  faixas: FaixaDeIdade[];
  /** Quanto os maiores devedores (até QUANTOS_MAIORES) somam. */
  maiores: { quantas: number; valor: number };
  /** Fiado novo e dinheiro de fiado que voltou, nos últimos 30 dias. */
  vendido30: number;
  recebido30: number;
  /** O que ficou devendo nas contas dadas como perdidas. */
  perdido: number;
  /** Tudo o que já foi vendido fiado — a base da taxa de perda. */
  vendidoFiado: number;
}

/**
 * Os números do fiado da LOJA INTEIRA. Recebe as pessoas de pessoasDoFiado —
 * com todas as contas, quitadas inclusive — e os mesmos acertos.
 */
export function resumoDoFiado(
  pessoas: readonly PessoaDoFiado[], acertos: readonly AcertoDoFiado[], hoje: string,
): ResumoDoFiado {
  const contas = pessoas.flatMap((p) => p.contas);
  const abertas = contas.filter(estaAberta);
  const devendo = pessoas.filter((p) => p.saldo > 0);

  const faixas: FaixaDeIdade[] = FAIXAS_DE_IDADE.map((f) => ({
    rotulo: f.rotulo, valor: 0, contas: 0, passouDoCostume: f.ate > DIAS_PARA_ATRASO,
  }));
  for (const f of abertas) {
    const i = FAIXAS_DE_IDADE.findIndex((faixa) => f.dias_em_aberto <= faixa.ate);
    const faixa = faixas[i];
    if (!faixa) continue;
    faixa.valor += Number(f.saldo);
    faixa.contas += 1;
  }

  const maiores = [...devendo].sort((a, b) => b.saldo - a.saldo).slice(0, QUANTOS_MAIORES);

  // A janela começa 29 dias antes de hoje: com hoje, são 30 dias.
  const inicio = somarDias(hoje, -(DIAS_DA_JANELA - 1));
  // Só acertos de contas que a lista conhece: a conta de venda CANCELADA não
  // vem em listar_fiados, e o acerto dela não pode aparecer como dinheiro de
  // fiado que voltou.
  const conhecidas = new Set(contas.map((f) => f.id));

  return {
    totalAberto: somar(abertas, (f) => Number(f.saldo)),
    contasAbertas: abertas.length,
    pessoasDevendo: devendo.length,
    atrasado: somar(pessoas, (p) => p.saldoAtrasado),
    contasAtrasadas: somar(pessoas, (p) => p.atrasadas),
    pessoasAtrasadas: pessoas.filter((p) => p.atrasadas > 0).length,
    prazoMedio: prazoPesado(contas, acertosPorConta(acertos.filter((a) => conhecidas.has(a.conta_id)))),
    idadeMedia: idadePesada(abertas),
    faixas,
    maiores: { quantas: maiores.length, valor: somar(maiores, (p) => p.saldo) },
    vendido30: somar(contas.filter((f) => diaDaVenda(f.vendida_em) >= inicio), (f) => Number(f.valor)),
    recebido30: somar(
      acertos.filter((a) => conhecidas.has(a.conta_id) && a.recebido_em >= inicio),
      (a) => Number(a.valor),
    ),
    perdido: somar(pessoas, (p) => p.perdido),
    vendidoFiado: somar(contas, (f) => Number(f.valor)),
  };
}

// --- Ordem da lista de pessoas ---------------------------------------------------

export type OrdemPessoas = 'maior' | 'antiga' | 'atrasado' | 'lento' | 'nome';

export const ORDENS_PESSOAS = [
  // Quem deve MAIS em cima é o padrão: é a primeira pergunta de quem abre a
  // lista por pessoa ("quem está me devendo mais?").
  { id: 'maior', rotulo: 'Deve mais' },
  { id: 'antiga', rotulo: 'Dívida mais antiga' },
  { id: 'atrasado', rotulo: 'Mais atrasado (R$)' },
  { id: 'lento', rotulo: 'Demora mais para pagar' },
  { id: 'nome', rotulo: 'Nome (A–Z)' },
] as const satisfies ReadonlyArray<{ id: OrdemPessoas; rotulo: string }>;

/** Ordena sem mexer na lista original. Nulo vai para o FIM em qualquer ordem
 *  (porNumero): quem nunca pagou não tem prazo, e não é o "mais rápido". */
export function ordenarPessoas(pessoas: readonly PessoaDoFiado[], ordem: OrdemPessoas): PessoaDoFiado[] {
  return [...pessoas].sort((a, b) => {
    if (ordem === 'nome') return porTexto(a.nome, b.nome);
    if (ordem === 'antiga') {
      return porNumero(a.diasMaisAntiga, b.diasMaisAntiga, 'desc')
        || porNumero(a.saldo, b.saldo, 'desc') || porTexto(a.nome, b.nome);
    }
    if (ordem === 'atrasado') {
      return porNumero(a.saldoAtrasado, b.saldoAtrasado, 'desc')
        || porNumero(a.diasMaisAntiga, b.diasMaisAntiga, 'desc') || porTexto(a.nome, b.nome);
    }
    if (ordem === 'lento') {
      return porNumero(a.prazoMedio, b.prazoMedio, 'desc')
        || porNumero(a.saldo, b.saldo, 'desc') || porTexto(a.nome, b.nome);
    }
    return porNumero(a.saldo, b.saldo, 'desc') || porTexto(a.nome, b.nome);
  });
}

// --- Texto ----------------------------------------------------------------------

/** "1 dia", "16 dias" — arredondado: meio dia não ajuda ninguém a cobrar. */
export function textoDias(n: number): string {
  const d = Math.round(n);
  return `${d} ${d === 1 ? 'dia' : 'dias'}`;
}

const PORCENTO_INTEIRO = new Intl.NumberFormat('pt-BR', { style: 'percent', maximumFractionDigits: 0 });
const PORCENTO_FINO = new Intl.NumberFormat('pt-BR', { style: 'percent', maximumFractionDigits: 1 });

/** "46%", e com uma casa abaixo de 10% ("2,4%") — perda de 2,4% arredondada
 *  para "2%" esconderia o que se quer ver. Sem base, "—". */
export function textoPorcento(parte: number, todo: number): string {
  if (!(todo > 0)) return '—';
  const fracao = parte / todo;
  return (fracao < 0.1 ? PORCENTO_FINO : PORCENTO_INTEIRO).format(fracao);
}
